// client/src/pages/Landing.js
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";

const faqItems = [
  {
    q: "What is an SMM Panel and why do you need one?",
    a: "An SMM panel lets you automate social media growth across platforms like Instagram, YouTube, TikTok and more. With smsorbit you get a single dashboard to place orders, track status and use reseller APIs instead of managing dozens of providers manually.",
  },
  {
    q: "What is an SMM service?",
    a: "SMM services are actions like followers, likes, views, comments or subscribers that help boost your visibility. Each smsorbit service clearly lists speed, quality and refill information so you know exactly what you’re buying.",
  },
  {
    q: "What does Mass Order mean?",
    a: "Mass Order lets you paste multiple links in one shot and create many orders at once. It’s useful for agencies and resellers who manage multiple posts or clients and don’t want to create each order manually.",
  },
  {
    q: "What does Drip-feed mean?",
    a: "Drip-feed allows you to spread delivery over time so growth looks natural. For example: instead of 2000 likes at once, you can schedule 200 likes per day for 10 days.",
  },
];

export default function Landing() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [openFaq, setOpenFaq] = useState(null);

  const handleHeroLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    if (!email || !password) {
      setLoginError("Please enter email and password");
      return;
    }
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) setLoginError(res.message || "Login failed");
  };

  const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.6, delay },
  });

  return (
    <div className="min-h-screen bg-[#05040f] text-white">
    
      {/* ================= HERO ================= */}
      <section className="w-full px-6 md:px-20 pt-20 pb-24 bg-gradient-to-br from-[#05040f] via-[#07071a] to-[#05040f]">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          {/* LEFT BLOCK */}
          <motion.div {...fadeUp(0.05)}>
            <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/5 border border-purple-500/40 text-xs md:text-sm text-purple-200 mb-6">
              <span className="w-2 h-2 rounded-full bg-emerald-400 mr-2" />
              India’s Premium SMM Panel for Individuals, Agencies & Resellers
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
              Grow your <span className="text-purple-300">Social Media</span>{" "}
              with <span className="text-purple-400">SMSOrbit</span>{" "}
            </h1>

            <p className="text-gray-300 mt-6 text-base md:text-lg max-w-xl">
              Instant social media growth for Instagram, YouTube, Telegram,
              WhatsApp and more — optimized pricing, 24/7 uptime, reseller API &
              fully automated order tracking.
            </p>

            <div className="flex flex-wrap gap-4 mt-8">
              <Link
                to="/register"
                className="px-8 py-3 rounded-xl bg-purple-600 hover:bg-purple-700 shadow-lg shadow-purple-500/40 text-sm md:text-base font-semibold"
              >
                Start for Free
              </Link>
              <Link
                to="/services"
                className="px-8 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/15 text-sm md:text-base"
              >
                View Services
              </Link>
            </div>
          </motion.div>

          {/* RIGHT LOGIN CARD */}
          <motion.div
            {...fadeUp(0.15)}
            className="bg-[#141428]/95 border border-white/10 rounded-2xl p-8 shadow-2xl shadow-black/50"
          >
            <h3 className="text-2xl font-semibold">Login to your panel</h3>
            <p className="text-sm text-gray-300 mt-2">
              Existing users can login directly. New here?{" "}
              <Link to="/register" className="text-purple-300 underline">
                Create account
              </Link>
            </p>

            {loginError && (
              <div className="mt-4 rounded-xl bg-red-500/10 border border-red-500/40 px-4 py-2 text-sm text-red-200">
                {loginError}
              </div>
            )}

            <form className="mt-6 space-y-4" onSubmit={handleHeroLogin}>
              <div>
                <label className="block text-xs text-gray-300 mb-1">
                  Email address
                </label>
                <input
                  type="email"
                  className="w-full px-4 py-2.5 rounded-xl bg-black/40 border border-white/15 text-sm outline-none focus:border-purple-400"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-xs text-gray-300 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPass ? "text" : "password"}
                    className="w-full px-4 py-2.5 pr-16 rounded-xl bg-black/40 border border-white/15 text-sm outline-none focus:border-purple-400"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass((s) => !s)}
                    className="absolute inset-y-0 right-3 flex items-center text-xs text-gray-300 hover:text-white"
                  >
                    {showPass ? "Hide" : "Show"}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 py-3 rounded-xl bg-purple-600 hover:bg-purple-700 disabled:opacity-60 disabled:cursor-not-allowed font-semibold text-sm md:text-base shadow-lg shadow-purple-500/40"
              >
                {loading ? "Logging in..." : "Login"}
              </button>
            </form>

            <p className="mt-4 text-[11px] text-gray-400">
              By using SMSOrbit you agree to our{" "}
              <Link to="/terms" className="underline">
                Terms of Service
              </Link>
              .
            </p>
          </motion.div>
        </div>
      </section>

      {/* ================= MINI STATS ================= */}
      <section className="px-6 md:px-20 bg-[#05040f]">
        <motion.div
          {...fadeUp(0.1)}
          className="max-w-5xl mx-auto -mt-10 mb-10 p-5 rounded-2xl bg-black/30 backdrop-blur-xl border border-white/10 grid grid-cols-2 md:grid-cols-4 gap-4 text-center"
        >
          <div>
            <p className="text-purple-400 text-xl font-bold">23K+</p>
            <p className="text-xs text-slate-400">Total Orders</p>
          </div>
          <div>
            <p className="text-purple-400 text-xl font-bold">4.3K+</p>
            <p className="text-xs text-slate-400">Active Users</p>
          </div>
          <div>
            <p className="text-purple-400 text-xl font-bold">1.2K+</p>
            <p className="text-xs text-slate-400">Live Services</p>
          </div>
          <div>
            <p className="text-purple-400 text-xl font-bold">98.4%</p>
            <p className="text-xs text-slate-400">Success Rate</p>
          </div>
        </motion.div>
      </section>

      {/* ================= HOW TO USE / WORK PROCESS ================= */}
      <section className="py-12 px-6 md:px-20 bg-[#05040f]">
        <motion.div
          {...fadeUp(0.1)}
          className="max-w-6xl mx-auto text-center mb-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold">
            How Our Panel <span className="text-purple-400">Works</span>
          </h2>
          <p className="text-gray-400 mt-3 text-sm md:text-base max-w-2xl mx-auto">
            Simple 5-step process from signup to results — designed for
            agencies, resellers and direct clients.
          </p>
          {/* timeline line */}
          <div className="hidden md:block h-1 bg-gradient-to-r from-purple-500 via-sky-400 to-purple-500 rounded-full mb-8 relative">
            <div className="absolute -top-1 left-1/4 w-3 h-3 rounded-full bg-purple-300" />
            <div className="absolute -top-1 left-1/2 w-3 h-3 rounded-full bg-purple-300" />
            <div className="absolute -top-1 left-3/4 w-3 h-3 rounded-full bg-purple-300" />
          </div>
        </motion.div>

        <motion.div
          {...fadeUp(0.15)}
          className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-6"
        >
          {[
            [
              "Create Your Free Account",
              "Register on smsorbit and log in. The dashboard works smoothly on both mobile and desktop.",
            ],
            [
              "Add Balance",
              "Top up using UPI, cards, net banking, Razorpay or PayPal. Payments are secure and processed instantly.",
            ],
            [
              "Select Your Service",
              "Choose from Instagram, YouTube, TikTok, Facebook, LinkedIn, Telegram, Spotify and more.",
            ],
            [
              "Place Your Order",
              "Paste your link, choose quantity and confirm. Most services start within seconds.",
            ],
            [
              "Monitor Growth",
              "Track orders in real time. Refills and warranty-based services are handled automatically.",
            ],
          ].map(([title, desc], idx) => (
            <div
              key={idx}
              className="relative flex-1 min-w-[210px] rounded-3xl bg-[#090912] border border-white/5 px-6 py-6 text-left"
            >
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-purple-600 text-sm font-bold mb-3">
                {idx + 1}
              </div>
              <h3 className="font-semibold mb-1 text-sm md:text-base">
                {title}
              </h3>
              <p className="text-xs md:text-sm text-gray-300">{desc}</p>
            </div>
          ))}
        </motion.div>
      </section>

      {/* ================= POWERFUL FEATURES ================= */}
      <section className="py-20 px-6 md:px-20 bg-[#05040f]">
        <motion.h2
          {...fadeUp(0.05)}
          className="text-center text-3xl md:text-4xl font-bold"
        >
          Powerful <span className="text-purple-400">Features</span>
        </motion.h2>

        <motion.div
          {...fadeUp(0.15)}
          className="mt-14 grid md:grid-cols-3 gap-8 max-w-6xl mx-auto"
        >
          {[
            ["⚡", "Instant Order Processing"],
            ["📈", "Real-Time Tracking"],
            ["💰", "Reseller Pricing"],
            ["🛠️", "API Support"],
            ["🔗", "Auto Routing"],
            ["🤖", "24/7 Automation"],
          ].map(([icon, title]) => (
            <motion.div
              whileHover={{ y: -4, scale: 1.01 }}
              key={title}
              className="flex items-center gap-4 px-8 py-8 rounded-3xl bg-[#090912] border border-white/5 shadow-[0_0_30px_rgba(0,0,0,0.4)]"
            >
              <span className="text-3xl md:text-4xl">{icon}</span>
              <div className="text-lg font-semibold">{title}</div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ================= TOP PLATFORMS ================= */}
      <section className="py-18 px-6 md:px-20 bg-[#05040f]">
        <motion.div {...fadeUp(0.05)} className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold">Top Platforms</h2>
          <p className="text-gray-400 mt-3">
            All major social networks supported, with smart routing behind the
            scenes.
          </p>

          <div className="flex flex-wrap justify-center gap-4 mt-10">
            {[
              "Instagram",
              "YouTube",
              "TikTok",
              "Facebook",
              "Twitter",
              "Spotify",
              "LinkedIn",
              "Telegram",
            ].map((p) => (
              <motion.div
                key={p}
                whileHover={{ y: -2, scale: 1.02 }}
                className="px-6 py-2.5 rounded-full bg-white/5 border border-white/10 text-sm"
              >
                {p}
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ================= WHY CHOOSE SMSOrbit ================= */}
      <section className="py-20 px-6 md:px-20 bg-[#05040f]">
        <motion.h2
          {...fadeUp(0.05)}
          className="text-center text-3xl md:text-4xl font-bold"
        >
          Why Choose <span className="text-purple-400">SMSOrbit?</span>
        </motion.h2>

        <motion.div
          {...fadeUp(0.15)}
          className="mt-12 max-w-6xl mx-auto grid md:grid-cols-2 gap-8"
        >
          {[
            ["Ultra-Fast Delivery", "Premium providers for instant speed."],
            [
              "Enterprise Reliability",
              "99.9% uptime with fully monitored automation.",
            ],
            [
              "Smart Automation",
              "Auto-sync, routing and status checks running 24/7.",
            ],
            ["24/7 Support", "Ticket-based support for you and your clients."],
          ].map(([title, desc]) => (
            <motion.div
              key={title}
              whileHover={{ y: -4, scale: 1.01 }}
              className="px-8 py-8 rounded-3xl bg-[#090912] border border-white/5"
            >
              <h3 className="text-xl font-semibold mb-2">{title}</h3>
              <p className="text-gray-300 text-sm">{desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ================= FAQ (ACCORDION) ================= */}
      <section className="py-20 px-6 md:px-20 bg-[#05040f]">
        <motion.h2
          {...fadeUp(0.05)}
          className="text-center text-3xl md:text-4xl font-bold"
        >
          FAQ (Frequently Asked Questions)
        </motion.h2>

        <motion.div
          {...fadeUp(0.15)}
          className="mt-10 max-w-4xl mx-auto space-y-4"
        >
          {faqItems.map((item, idx) => {
            const isOpen = openFaq === idx;
            return (
              <div
                key={idx}
                className="rounded-3xl bg-[#090912] border border-white/5 overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() =>
                    setOpenFaq((current) => (current === idx ? null : idx))
                  }
                  className="w-full flex items-center justify-between px-6 py-4 text-left"
                >
                  <span className="font-semibold text-sm md:text-base">
                    {item.q}
                  </span>
                  <span className="text-purple-300 text-xl ml-4">
                    {isOpen ? "−" : "+"}
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="px-6 pb-4"
                    >
                      <p className="text-sm text-gray-300">{item.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </motion.div>
      </section>
    </div>
  );
}
